import React, { useMemo, useState } from "react";
import { AlertTriangle, Lock, Search, UtensilsCrossed, Users } from "lucide-react";

import { Card, EmptyState, PageHeader, Pill, StatCard, TabSkeleton } from "./ui";
import { dietaryCategory } from "../../TablePlan/utils/dietary";
import { STATUS_LABELS, normalizeStatus } from "../guestStats";

const STATUS_TONE = { confirmed: "success", pending: "warning", declined: "danger" };
// Allergies first: they are the only constraint where a miss is a safety issue.
const ORDER = ["allergy", "intolerance", "gluten", "vegan", "vegetarian", "halal", "kosher", "other"];
const LABELS = { allergy: "Allergies", intolerance: "Intolérances", gluten: "Sans gluten", vegan: "Vegan", vegetarian: "Végétariens", halal: "Halal", kosher: "Casher", other: "Autres régimes" };
const COLORS = { allergy: "#dc2626", intolerance: "#c2410c", gluten: "#d97706", vegan: "#0d9488", vegetarian: "#16a34a", halal: "#2563eb", kosher: "#7c3aed", other: "#6b7280" };

const normalize = (value) => String(value || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** Group the guests that have a dietary note, by kind of constraint. */
export function groupDietary(guests = []) {
  const groups = Object.fromEntries(ORDER.map((key) => [key, []]));
  guests.forEach((guest) => {
    const category = dietaryCategory(guest.dietary);
    if (category) groups[category.key].push({ ...guest, category });
  });
  return groups;
}

/**
 * Caterer view: who has which constraint, allergies first, with the figures a
 * kitchen needs. Read-only; no contact details.
 */
export default function DietaryTab({ guests }) {
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState("all");
  const groups = useMemo(() => groupDietary(guests || []), [guests]);

  if (!guests) return <TabSkeleton />;

  const all = ORDER.flatMap((key) => groups[key]);
  const attending = all.filter((guest) => normalizeStatus(guest.rsvp_status) === "confirmed").length;
  const allergies = groups.allergy;
  const needle = normalize(query.trim());
  const matches = (guest) => !needle || normalize(guest.name).includes(needle) || normalize(guest.dietary).includes(needle);
  const shownKeys = ORDER.filter((key) => (kind === "all" || kind === key) && groups[key].some(matches));

  return (
    <div className="w-full space-y-6 pb-8">
      <PageHeader
        eyebrow="Vue traiteur"
        title="Régimes & allergies"
        subtitle="Seuls les convives ayant une restriction sont listés. Aucune coordonnée n'est partagée."
        actions={<Pill tone="neutral"><Lock className="h-3 w-3" aria-hidden="true" />Lecture seule</Pill>}
      />

      {allergies.length > 0 ? (
        <div role="alert" className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-rose-900">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <p className="text-sm"><strong>{allergies.length} convive{allergies.length > 1 ? "s" : ""} avec une allergie</strong> : à signaler à la cuisine avant le service.</p>
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Convives concernés" value={all.length} hint="Avec une restriction" icon={Users} />
        <StatCard label="Dont confirmés" value={attending} hint={`${all.length - attending} en attente ou absents`} tone="success" icon={UtensilsCrossed} />
        <StatCard label="Allergies" value={allergies.length} hint={allergies.length ? "À traiter en priorité" : "Aucune signalée"} tone={allergies.length ? "danger" : "neutral"} icon={AlertTriangle} />
        <StatCard label="Autres régimes" value={all.length - allergies.length} hint="Végétarien, sans gluten…" tone="info" icon={UtensilsCrossed} />
      </div>

      {all.length === 0 ? (
        <EmptyState icon={UtensilsCrossed} title="Aucune restriction signalée" text="Dès qu'un invité indiquera un régime ou une allergie, il apparaîtra ici." />
      ) : (
        <Card className="!p-0 overflow-hidden">
          <div className="space-y-3 border-b border-[#f0e9dc] p-4 md:p-5">
            <label className="relative block">
              <span className="sr-only">Rechercher</span>
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8b7f70]" aria-hidden="true" />
              <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Rechercher un convive ou une restriction" className="w-full rounded-xl border border-[#e2d9c8] bg-[#fbf8f2] py-2.5 pl-10 pr-3 text-sm text-[#211d19] outline-none placeholder:text-[#8b7f70] focus:border-[#5f7d68] focus:bg-white" />
            </label>
            <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="tablist" aria-label="Type de restriction">
              <button type="button" role="tab" aria-selected={kind === "all"} onClick={() => setKind("all")} className={`shrink-0 rounded-full border px-3.5 py-1.5 text-xs font-semibold ${kind === "all" ? "border-[#211d19] bg-[#211d19] text-white" : "border-[#e2d9c8] bg-white text-[#5b5247] hover:bg-[#f6f1e8]"}`}>Toutes {all.length}</button>
              {ORDER.filter((key) => groups[key].length).map((key) => (
                <button key={key} type="button" role="tab" aria-selected={kind === key} onClick={() => setKind(key)} className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-semibold ${kind === key ? "border-[#211d19] bg-[#211d19] text-white" : "border-[#e2d9c8] bg-white text-[#5b5247] hover:bg-[#f6f1e8]"}`}>
                  <i className="h-2 w-2 rounded-full" style={{ backgroundColor: COLORS[key] }} />{LABELS[key]} {groups[key].length}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-6 p-4 md:p-5">
            {shownKeys.length === 0 ? <EmptyState icon={Search} title="Aucun résultat" text="Modifiez la recherche ou le type de restriction." /> : null}
            {shownKeys.map((key) => (
              <section key={key} aria-label={LABELS[key]}>
                <h3 className="mb-3 flex items-center gap-2 text-sm font-bold text-[#211d19]">
                  <i className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: COLORS[key] }} />{LABELS[key]}
                  <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs font-semibold text-[#5b5247]">{groups[key].length}</span>
                </h3>
                <ul className="grid gap-3 md:grid-cols-2">
                  {groups[key].filter(matches).map((guest) => {
                    const state = normalizeStatus(guest.rsvp_status);
                    return (
                      <li key={guest.id} className={`rounded-2xl border p-4 ${key === "allergy" ? "border-rose-200 bg-rose-50/60" : "border-[#e9e1d4] bg-white"}`}>
                        <div className="flex items-start justify-between gap-3">
                          <strong className="min-w-0 break-words text-[#211d19]">{guest.name}</strong>
                          <Pill tone={STATUS_TONE[state]}>{STATUS_LABELS[state]}</Pill>
                        </div>
                        <p className="mt-2 whitespace-pre-wrap break-words text-sm text-[#3f372f]">{guest.dietary}</p>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
